// ============================================
// Admin Event Session Health
// ============================================

import React from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../config/firebase';
import { Event } from '../../../types';

interface SessionHealthProps {
  event: Event;
}

interface HealthIssue {
  type: string;
  severity: 'high' | 'medium' | 'low';
  therapistId?: string;
  therapistName?: string;
  sessionId?: string;
  requestId?: string;
  message: string;
  data: Record<string, any>;
}

interface HealthData {
  activeSessions: number;
  totalTherapists: number;
  onDutyTherapists: number;
  issues: HealthIssue[];
}

export default function SessionHealth({ event }: SessionHealthProps) {
  const [healthData, setHealthData] = React.useState<HealthData | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [clearing, setClearing] = React.useState<string | null>(null);
  const [actionInProgress, setActionInProgress] = React.useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = React.useState<Date>(new Date());

  const fetchHealth = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const getSessionHealth = httpsCallable(functions, 'getSessionHealth');
      const result = await getSessionHealth({ eventId: event.id });
      setHealthData(result.data as HealthData);
      setLastRefresh(new Date());
    } catch (err: any) {
      console.error('Error fetching session health:', err);
      setError(err.message || 'Failed to fetch session health');
    } finally {
      setLoading(false);
    }
  }, [event.id]);

  React.useEffect(() => {
    fetchHealth();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchHealth, 30000);
    return () => clearInterval(interval);
  }, [fetchHealth]);

  const clearTherapistState = async (therapistId: string, therapistName: string) => {
    if (!window.confirm(`Clear stuck state for ${therapistName}? This will reset their activeSessionId and currentAcceptedRequestId.`)) {
      return;
    }

    setClearing(therapistId);
    try {
      const clearState = httpsCallable(functions, 'clearTherapistState');
      const result = await clearState({ eventId: event.id, therapistId });
      console.log('Clear state result:', result.data);
      alert(`State cleared for ${therapistName}`);
      // Refresh health data
      await fetchHealth();
    } catch (err: any) {
      console.error('Error clearing state:', err);
      alert(err.message || 'Failed to clear therapist state');
    } finally {
      setClearing(null);
    }
  };

  const forceCompleteSession = async (sessionId: string, therapistName: string) => {
    if (!window.confirm(`Force complete session for ${therapistName}? This will calculate payouts based on actual duration and mark the session as completed.`)) {
      return;
    }

    setActionInProgress(`complete-${sessionId}`);
    try {
      const forceComplete = httpsCallable(functions, 'adminForceCompleteSession');
      const result = await forceComplete({ eventId: event.id, sessionId }) as any;
      console.log('Force complete result:', result.data);
      alert(`Session completed for ${therapistName}. Duration: ${result.data.session?.actualDuration || 0} minutes`);
      await fetchHealth();
    } catch (err: any) {
      console.error('Error force completing session:', err);
      alert(err.message || 'Failed to force complete session');
    } finally {
      setActionInProgress(null);
    }
  };

  const voidSession = async (sessionId: string, therapistName: string) => {
    const reason = window.prompt(`Void session for ${therapistName}? Enter reason (required):`);
    if (!reason) {
      return;
    }

    setActionInProgress(`void-${sessionId}`);
    try {
      const voidFn = httpsCallable(functions, 'adminVoidSession');
      const result = await voidFn({ eventId: event.id, sessionId, reason }) as any;
      console.log('Void session result:', result.data);
      alert(`Session voided for ${therapistName}`);
      await fetchHealth();
    } catch (err: any) {
      console.error('Error voiding session:', err);
      alert(err.message || 'Failed to void session');
    } finally {
      setActionInProgress(null);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high':
        return '#ef4444';
      case 'medium':
        return '#f59e0b';
      case 'low':
        return '#3b82f6';
      default:
        return '#6b7280';
    }
  };

  const getIssueIcon = (type: string) => {
    switch (type) {
      case 'orphaned_session_reference':
        return '🔗';
      case 'session_but_off_duty':
        return '⚠️';
      case 'long_running_session':
        return '⏱️';
      case 'stale_heartbeat':
        return '💓';
      case 'long_pending_request':
        return '⏳';
      case 'session_not_linked':
        return '🔌';
      default:
        return '❓';
    }
  };

  if (loading && !healthData) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div className="spinner"></div>
        <p>Loading session health...</p>
      </div>
    );
  }

  if (error && !healthData) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p style={{ color: '#ef4444' }}>Error: {error}</p>
        <button onClick={fetchHealth} className="btn btn-primary">
          Retry
        </button>
      </div>
    );
  }

  const highIssues = healthData?.issues.filter(i => i.severity === 'high') || [];
  const mediumIssues = healthData?.issues.filter(i => i.severity === 'medium') || [];
  const lowIssues = healthData?.issues.filter(i => i.severity === 'low') || [];

  return (
    <div className="session-health">
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem',
      }}>
        <h2 style={{ margin: 0 }}>Session Health</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <span style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
            Last updated: {lastRefresh.toLocaleTimeString()}
          </span>
          <button
            onClick={fetchHealth}
            className="btn btn-secondary"
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem',
      }}>
        <div style={{
          backgroundColor: 'var(--color-bg-secondary)',
          padding: '1.25rem',
          borderRadius: '12px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#22c55e' }}>
            {healthData?.activeSessions || 0}
          </div>
          <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
            Active Sessions
          </div>
        </div>

        <div style={{
          backgroundColor: 'var(--color-bg-secondary)',
          padding: '1.25rem',
          borderRadius: '12px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#3b82f6' }}>
            {healthData?.onDutyTherapists || 0}
          </div>
          <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
            On Duty
          </div>
        </div>

        <div style={{
          backgroundColor: 'var(--color-bg-secondary)',
          padding: '1.25rem',
          borderRadius: '12px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--color-text-secondary)' }}>
            {healthData?.totalTherapists || 0}
          </div>
          <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
            Total Therapists
          </div>
        </div>

        <div style={{
          backgroundColor: 'var(--color-bg-secondary)',
          padding: '1.25rem',
          borderRadius: '12px',
          textAlign: 'center',
        }}>
          <div style={{
            fontSize: '2rem',
            fontWeight: 700,
            color: highIssues.length > 0 ? '#ef4444' : mediumIssues.length > 0 ? '#f59e0b' : '#22c55e',
          }}>
            {healthData?.issues.length || 0}
          </div>
          <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
            Issues Found
          </div>
        </div>
      </div>

      {/* Issues List */}
      {healthData?.issues.length === 0 ? (
        <div style={{
          backgroundColor: '#22c55e20',
          border: '1px solid #22c55e',
          borderRadius: '12px',
          padding: '2rem',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✅</div>
          <div style={{ fontSize: '1.125rem', fontWeight: 600, color: '#22c55e' }}>
            All Systems Healthy
          </div>
          <div style={{ color: 'var(--color-text-secondary)', marginTop: '0.25rem' }}>
            No issues detected
          </div>
        </div>
      ) : (
        <div>
          {/* High Severity Issues */}
          {highIssues.length > 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{
                color: '#ef4444',
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: '0.75rem',
              }}>
                High Priority ({highIssues.length})
              </h3>
              {highIssues.map((issue, idx) => (
                <IssueCard
                  key={`high-${idx}`}
                  issue={issue}
                  getSeverityColor={getSeverityColor}
                  getIssueIcon={getIssueIcon}
                  onClearState={clearTherapistState}
                  onForceComplete={forceCompleteSession}
                  onVoidSession={voidSession}
                  clearing={clearing}
                  actionInProgress={actionInProgress}
                />
              ))}
            </div>
          )}

          {/* Medium Severity Issues */}
          {mediumIssues.length > 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{
                color: '#f59e0b',
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: '0.75rem',
              }}>
                Medium Priority ({mediumIssues.length})
              </h3>
              {mediumIssues.map((issue, idx) => (
                <IssueCard
                  key={`medium-${idx}`}
                  issue={issue}
                  getSeverityColor={getSeverityColor}
                  getIssueIcon={getIssueIcon}
                  onClearState={clearTherapistState}
                  onForceComplete={forceCompleteSession}
                  onVoidSession={voidSession}
                  clearing={clearing}
                  actionInProgress={actionInProgress}
                />
              ))}
            </div>
          )}

          {/* Low Severity Issues */}
          {lowIssues.length > 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{
                color: '#3b82f6',
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: '0.75rem',
              }}>
                Low Priority ({lowIssues.length})
              </h3>
              {lowIssues.map((issue, idx) => (
                <IssueCard
                  key={`low-${idx}`}
                  issue={issue}
                  getSeverityColor={getSeverityColor}
                  getIssueIcon={getIssueIcon}
                  onClearState={clearTherapistState}
                  onForceComplete={forceCompleteSession}
                  onVoidSession={voidSession}
                  clearing={clearing}
                  actionInProgress={actionInProgress}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface IssueCardProps {
  issue: HealthIssue;
  getSeverityColor: (severity: string) => string;
  getIssueIcon: (type: string) => string;
  onClearState: (therapistId: string, therapistName: string) => void;
  onForceComplete: (sessionId: string, therapistName: string) => void;
  onVoidSession: (sessionId: string, therapistName: string) => void;
  clearing: string | null;
  actionInProgress: string | null;
}

function IssueCard({ issue, getSeverityColor, getIssueIcon, onClearState, onForceComplete, onVoidSession, clearing, actionInProgress }: IssueCardProps) {
  const [expanded, setExpanded] = React.useState(false);

  const canClear = issue.type === 'orphaned_session_reference' && issue.therapistId;
  const canForceComplete = (issue.type === 'session_not_linked' || issue.type === 'long_running_session' || issue.type === 'session_but_off_duty') && issue.sessionId;
  const canVoid = canForceComplete;

  return (
    <div style={{
      backgroundColor: 'var(--color-bg-secondary)',
      border: `1px solid ${getSeverityColor(issue.severity)}40`,
      borderRadius: '8px',
      padding: '1rem',
      marginBottom: '0.75rem',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: '1rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', flex: 1 }}>
          <span style={{ fontSize: '1.25rem' }}>{getIssueIcon(issue.type)}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
              {issue.therapistName || issue.type.replace(/_/g, ' ')}
            </div>
            <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
              {issue.message}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{
            fontSize: '0.75rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            color: getSeverityColor(issue.severity),
            backgroundColor: `${getSeverityColor(issue.severity)}20`,
            padding: '0.25rem 0.5rem',
            borderRadius: '4px',
          }}>
            {issue.severity}
          </span>
          <button
            onClick={() => setExpanded(!expanded)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--color-text-secondary)',
              padding: '0.25rem',
            }}
          >
            {expanded ? '▲' : '▼'}
          </button>
        </div>
      </div>

      {expanded && (
        <div style={{
          marginTop: '1rem',
          paddingTop: '1rem',
          borderTop: '1px solid var(--color-border)',
        }}>
          <div style={{
            backgroundColor: 'var(--color-bg)',
            padding: '0.75rem',
            borderRadius: '4px',
            fontFamily: 'monospace',
            fontSize: '0.75rem',
            marginBottom: '1rem',
            overflow: 'auto',
          }}>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
              {JSON.stringify(issue.data, null, 2)}
            </pre>
          </div>

          {/* Therapist comment if exists */}
          {issue.data?.therapistComment && (
            <div style={{
              backgroundColor: '#3b82f620',
              border: '1px solid #3b82f6',
              borderRadius: '4px',
              padding: '0.75rem',
              marginBottom: '1rem',
            }}>
              <div style={{ fontSize: '0.75rem', color: '#3b82f6', fontWeight: 600, marginBottom: '0.25rem' }}>
                Therapist Comment:
              </div>
              <div style={{ fontSize: '0.875rem' }}>
                {issue.data.therapistComment}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {canClear && (
              <button
                onClick={() => onClearState(issue.therapistId!, issue.therapistName || 'Unknown')}
                className="btn btn-warning"
                disabled={clearing === issue.therapistId}
                style={{ width: '100%' }}
              >
                {clearing === issue.therapistId ? 'Clearing...' : 'Clear Stuck State'}
              </button>
            )}

            {canForceComplete && (
              <button
                onClick={() => onForceComplete(issue.sessionId!, issue.therapistName || 'Unknown')}
                className="btn btn-success"
                disabled={actionInProgress === `complete-${issue.sessionId}`}
                style={{ width: '100%' }}
              >
                {actionInProgress === `complete-${issue.sessionId}` ? 'Completing...' : 'Force Complete Session'}
              </button>
            )}

            {canVoid && (
              <button
                onClick={() => onVoidSession(issue.sessionId!, issue.therapistName || 'Unknown')}
                className="btn btn-danger"
                disabled={actionInProgress === `void-${issue.sessionId}`}
                style={{ width: '100%' }}
              >
                {actionInProgress === `void-${issue.sessionId}` ? 'Voiding...' : 'Void Session'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
